import type { CONSORS_NOTES_REQUEST_STATUS } from 'cpool-constants';
import { CONSORS_NOTES_REQUEST_FAILURE_REASONS, CONSORS_NOTES_REQUEST_STATUSES } from 'cpool-constants';
import { sleep } from 'cpool-utils';
import { type TFunction } from 'i18next';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { API_EXTERNAL } from '~/config';
import { fetcher } from '~/queryUtils/useQuery';

const schemaConsorsNotesRequestStatus = z.object({
    errorMessage: z.string().nullish(),
    failureReason: z.nativeEnum(CONSORS_NOTES_REQUEST_FAILURE_REASONS).nullish(),
    loanRequestId: z.string(),
    status: z.nativeEnum(CONSORS_NOTES_REQUEST_STATUSES),
});
export type SchemaConsorsNotesRequestStatus = z.infer<typeof schemaConsorsNotesRequestStatus>;

export const verifyRequestConsorsNotesStatus = async ({
    loanRequestId,
    position,
    subscriptionIdentifier,
    t,
    waitTime = API_EXTERNAL.CONSORS.SUBSCRIPTION_REQUESTED.RETRY_INTERVAL,
}: {
    loanRequestId: string;
    waitTime?: number;
    position?: number;
    t: TFunction<'de' | 'en', undefined>;
    subscriptionIdentifier: string;
}) => {
    try {
        let consorsRefreshNoteStatus: CONSORS_NOTES_REQUEST_STATUS = CONSORS_NOTES_REQUEST_STATUSES.PENDING;
        let response: SchemaConsorsNotesRequestStatus | null = null;
        let counter = 0;
        while (
            consorsRefreshNoteStatus === CONSORS_NOTES_REQUEST_STATUSES.PENDING &&
            counter < API_EXTERNAL.CONSORS.SUBSCRIPTION_REQUESTED.MAX_RETRIES
        ) {
            // eslint-disable-next-line no-await-in-loop
            await sleep(waitTime);
            // eslint-disable-next-line no-await-in-loop
            response = await fetcher({
                meta: {
                    queryUrl: `/query/loan-request/getConsorsNotesRequestStatus?id=${loanRequestId}&subscriptionIdentifier=${subscriptionIdentifier}&position=${position}`,
                    schema: schemaConsorsNotesRequestStatus,
                },
            });

            if (!response) {
                toast.error(t('CONSORS_NOTES.TOASTS.NOTES_REQUEST_FAILED'));
                return;
            }
            consorsRefreshNoteStatus = response.status;
            counter += 1;
        }
        if (consorsRefreshNoteStatus === CONSORS_NOTES_REQUEST_STATUSES.PENDING) {
            toast.error(t('CONSORS_NOTES.TOASTS.NOTES_REQUEST_TIMEOUT'));
            return;
        }
        if (consorsRefreshNoteStatus === CONSORS_NOTES_REQUEST_STATUSES.ERROR || consorsRefreshNoteStatus === null) {
            toast.error(
                <>
                    <div>{t('CONSORS_NOTES.TOASTS.NOTES_REQUEST_FAILED')}</div>
                    <div>{`${t('CONSORS_NOTES.TOASTS.FAILURE_REASON')}: ${t(
                        // @ts-expect-error - TS cannot regognize this
                        `CONSORS_NOTES.TOASTS.FAILURE_REASONS.${response?.failureReason}`,
                    )}`}</div>
                </>,
            );
            return;
        }
        // at this point we know that the status is SUCCESS (with or without changes)
        if (consorsRefreshNoteStatus === CONSORS_NOTES_REQUEST_STATUSES.NO_CHANGE) {
            toast.info(t('CONSORS_NOTES.TOASTS.NOTES_REQUEST_NO_CHANGE'));
            return;
        }
        toast.success(t('CONSORS_NOTES.TOASTS.NOTES_REQUEST_SUCCESS'));
    } catch {
        toast.error(t('CONSORS_NOTES.TOASTS.NOTES_REQUEST_FAILED'));
    }
};
